import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Flex, Tooltip,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiWifi, FiActivity, FiZap, FiArrowUp, FiArrowDown,
} from 'react-icons/fi';

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  };
  const instanceId = localStorage.getItem('instance_id');
  if (instanceId) headers['X-Tenant-ID'] = instanceId;
  return headers;
};

const apiFetch = (url) =>
  fetch(url, { headers: getAuthHeaders() }).then(r => r.json());

const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Group events into N-minute buckets for the activity chart. Returns newest-last.
function buildActivityBuckets(events, bucketMinutes = 5, bucketCount = 12) {
  const now = Math.floor(Date.now() / 1000);
  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const end = now - (bucketCount - 1 - i) * bucketMinutes * 60;
    const start = end - bucketMinutes * 60;
    return { start, end, connects: 0, disconnects: 0 };
  });

  for (const ev of events) {
    for (const bucket of buckets) {
      if (ev.ts >= bucket.start && ev.ts < bucket.end) {
        if (ev.type === 'connect') bucket.connects++;
        else bucket.disconnects++;
        break;
      }
    }
  }

  return buckets.map((b) => ({
    label: formatTime(b.start),
    connects: b.connects,
    disconnects: b.disconnects,
  }));
}

function ActivityChart({ events }) {
  const buckets = buildActivityBuckets(events);
  const maxVal = Math.max(1, ...buckets.map(b => b.connects + b.disconnects));

  return (
    <Box>
      <HStack spacing={4} mb={3}>
        <HStack spacing={1}>
          <Box w={3} h={3} bg="green.400" borderRadius="sm" />
          <Text fontSize="xs" color="gray.500">Connect</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w={3} h={3} bg="gray.300" borderRadius="sm" />
          <Text fontSize="xs" color="gray.500">Disconnect</Text>
        </HStack>
        <Text fontSize="xs" color="gray.400">5-minute buckets · last hour</Text>
      </HStack>

      {/* Bar chart */}
      <Flex align="flex-end" gap="3px" h="80px" mb={1}>
        {buckets.map((b, i) => {
          const total = b.connects + b.disconnects;
          const heightPct = total === 0 ? 0 : Math.max(5, (total / maxVal) * 100);
          const connectFrac = total === 0 ? 0 : b.connects / total;
          return (
            <Tooltip
              key={i}
              label={`${b.label} — ${b.connects} connect, ${b.disconnects} disconnect`}
              fontSize="xs"
            >
              <Flex
                flex={1}
                direction="column-reverse"
                align="stretch"
                h={total > 0 ? `${heightPct}%` : '2px'}
                minH={total > 0 ? '5px' : '2px'}
                borderRadius="sm"
                overflow="hidden"
                cursor="default"
                bg={total === 0 ? 'gray.100' : undefined}
              >
                {total > 0 && (
                  <>
                    <Box style={{ flex: connectFrac }} bg="green.400" />
                    <Box style={{ flex: 1 - connectFrac }} bg="gray.300" />
                  </>
                )}
              </Flex>
            </Tooltip>
          );
        })}
      </Flex>

      {/* x-axis: first and last label */}
      <Flex justify="space-between" mt={1}>
        <Text fontSize="2xs" color="gray.400">{buckets[0]?.label}</Text>
        <Text fontSize="2xs" color="gray.400">now</Text>
      </Flex>
    </Box>
  );
}

function StatCard({ icon, label, value, sub, color = 'orange' }) {
  return (
    <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
      <HStack spacing={3} mb={1}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
        <Text fontSize="sm" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.05em">
          {label}
        </Text>
      </HStack>
      <Text fontSize="3xl" fontWeight="bold" color={`${color}.600`}>{value}</Text>
      {sub && <Text fontSize="xs" color="gray.400" mt={1}>{sub}</Text>}
    </Box>
  );
}

export default function WebSocketStatus() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch('/api/v1/admin-ui/websocket/')
      .then(d => {
        setStats(d);
        setLastRefreshed(new Date());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 5 s
  useEffect(() => {
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const active = stats?.active_connections ?? 0;
  const totalEver = stats?.total_connections_ever ?? 0;
  const recentEvents = stats?.recent_events ?? [];

  const connectCount = recentEvents.filter(e => e.type === 'connect').length;
  const disconnectCount = recentEvents.filter(e => e.type === 'disconnect').length;

  return (
    <Box>
      {/* Header */}
      <HStack justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">WebSocket Server Status</Heading>
          <Text color="gray.500">
            Real-time connection metrics and event log
            {lastRefreshed && ` — refreshed ${lastRefreshed.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <HStack spacing={3}>
          {loading && stats && <Spinner size="sm" color="orange.400" />}
          <Button leftIcon={<FiRefreshCw />} colorScheme="orange" size="sm"
            onClick={fetchData} isLoading={loading && !stats}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {loading && !stats ? (
        <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>
      ) : (
        <>
          {/* Stat cards */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={8}>
            <StatCard
              icon={FiWifi}
              label="Active Now"
              value={active}
              color={active > 0 ? 'teal' : 'gray'}
              sub={active === 1 ? '1 open connection' : `${active} open connections`}
            />
            <StatCard
              icon={FiZap}
              label="Total (lifetime)"
              value={totalEver}
              color="blue"
              sub="since last server restart"
            />
            <StatCard
              icon={FiArrowUp}
              label="Connects"
              value={connectCount}
              color="green"
              sub={`in last ${recentEvents.length} logged events`}
            />
            <StatCard
              icon={FiArrowDown}
              label="Disconnects"
              value={disconnectCount}
              color="gray"
              sub={`in last ${recentEvents.length} logged events`}
            />
          </SimpleGrid>

          {/* Activity chart */}
          <Box bg="white" borderRadius="lg" boxShadow="md" p={6} mb={6}>
            <HStack mb={5} spacing={2}>
              <Icon as={FiActivity} color="orange.500" />
              <Heading size="md">Connection Activity</Heading>
            </HStack>
            {recentEvents.length === 0 ? (
              <Box textAlign="center" py={8} color="gray.400">
                <Icon as={FiActivity} boxSize={8} mb={3} opacity={0.4} />
                <Text fontSize="sm">No activity yet. Events will appear once clients connect.</Text>
              </Box>
            ) : (
              <ActivityChart events={recentEvents} />
            )}
          </Box>

          {/* Connection event log */}
          <Box bg="white" borderRadius="lg" boxShadow="md" p={6}>
            <HStack mb={4} justify="space-between">
              <HStack spacing={2}>
                <Icon as={FiWifi} color="teal.500" />
                <Heading size="md">Connection Log</Heading>
                {recentEvents.length > 0 && (
                  <Badge colorScheme="gray">{recentEvents.length} events</Badge>
                )}
              </HStack>
              {recentEvents.length > 0 && (
                <HStack spacing={3} fontSize="xs" color="gray.500">
                  <HStack spacing={1}>
                    <Badge colorScheme="green" fontSize="2xs">connect</Badge>
                    <Text>{connectCount}</Text>
                  </HStack>
                  <HStack spacing={1}>
                    <Badge colorScheme="gray" fontSize="2xs">disconnect</Badge>
                    <Text>{disconnectCount}</Text>
                  </HStack>
                </HStack>
              )}
            </HStack>

            {recentEvents.length === 0 ? (
              <Box textAlign="center" py={8} color="gray.400">
                <Icon as={FiWifi} boxSize={8} mb={3} opacity={0.3} />
                <Text fontSize="sm">No connection events logged yet.</Text>
              </Box>
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Time</Th>
                      <Th>Event</Th>
                      <Th>Channel</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentEvents.map((e, i) => (
                      <Tr key={i} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap" color="gray.500" fontSize="xs">
                          {formatDateTime(e.ts)}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={e.type === 'connect' ? 'green' : 'gray'}
                            fontSize="2xs"
                          >
                            {e.type}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontFamily="mono" fontSize="xs" color="gray.600">
                            {e.room}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
